import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Text,
  Flex,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  IconButton,
  Spinner,
  SimpleGrid,
  Avatar,
  Textarea,
  Input,
  Circle,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdArrowForward,
  MdCalendarToday,
  MdAccessTime,
  MdPerson,
  MdLocalHospital,
  MdPhone,
  MdInfo,
  MdTimer,
  MdLocationOn,
  MdLocationCity,
  MdAddBox,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../api/client";
import { BASE_URL } from "config/api";
import AffectationMap from "../missions/components/AffectationMap";
import { fetchVehicleLocation as fetchVehicleLocationSecure } from "../../../api/external";
import { io } from "socket.io-client";
import { getMyUserProfile } from "../../../api/api";

const getSocketServerUrl = () => {
  try {
    return new URL(BASE_URL).origin;
  } catch (_) {
    return BASE_URL;
  }
};

export default function DossierRegulationDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const { isOpen: isAffectOpen, onOpen: onAffectOpen, onClose: onAffectClose } = useDisclosure();
  const [dossier, setDossier] = useState(null);
  const [patientById, setPatientById] = useState(null);
  const [usersById, setUsersById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("apercu");
  const [assigningMission, setAssigningMission] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [allAmbulanciers, setAllAmbulanciers] = useState([]);
  const [selectedAmbulanceId, setSelectedAmbulanceId] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const normalizedRole = String(currentUserRole || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
  const canTakeDecision = normalizedRole === "medecin regulateur";

  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      try {
        const res = await getMyUserProfile();
        setCurrentUserRole(res?.data?.rôle || res?.data?.role || "");
      } catch {
        setCurrentUserRole("");
      }
    };
    fetchCurrentUserRole();
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError("");
        const dossierRes = await apiClient.get(`${BASE_URL}/dossier/${id}`);
        const dossierData = dossierRes.data || null;
        setDossier(dossierData);

        if (!dossierData) return;

        const userIds = [
          ...new Set([dossierData.créé_par, dossierData.assigne, dossierData.doc_a, dossierData.doc_b].filter(Boolean)),
        ];

        const requests = [];

        if (dossierData.patient_id) {
          requests.push(
            apiClient
              .get(`${BASE_URL}/patients/${dossierData.patient_id}`)
              .then((res) => setPatientById(res.data || null))
              .catch(() => setPatientById(null))
          );
        } else {
          setPatientById(null);
        }

        if (userIds.length > 0) {
          requests.push(
            Promise.all(userIds.map((userId) => apiClient.get(`${BASE_URL}/users/${userId}`)))
              .then((responses) => {
                const nextMap = {};
                responses.forEach((res, idx) => {
                  const userId = userIds[idx];
                  nextMap[String(userId)] = res.data || null;
                });
                setUsersById(nextMap);
              })
              .catch(() => setUsersById({}))
          );
        } else {
          setUsersById({});
        }

        if (requests.length > 0) await Promise.all(requests);
      } catch (e) {
        setError("Impossible de charger les détails du dossier.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetails();
  }, [id]);

  const parseLoc = (value) => {
    if (!value) return null;
    if (typeof value === "object" && value.lat != null && value.lng != null) {
      const lat = Number(value.lat);
      const lng = Number(value.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
      return null;
    }
    const raw = String(value);
    const [latStr, lngStr] = raw.split(",");
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const missionLocation = useMemo(() => {
    return parseLoc(dossier?.loc) || parseLoc(dossier?.loc_dest);
  }, [dossier]);

  const selectedVehicle = useMemo(() => {
    return vehicles.find((v) => String(v.id) === String(selectedAmbulanceId)) || null;
  }, [vehicles, selectedAmbulanceId]);

  const fetchVehicleLocation = async (trackerId) => {
    if (!trackerId) return null;
    try {
      return await fetchVehicleLocationSecure(trackerId);
    } catch (e) {
      return null;
    }
  };

  const handleOpenAffectation = async () => {
    setSelectedAmbulanceId("");
    onAffectOpen();
    try {
      const [vehiclesRes, usersRes] = await Promise.all([
        apiClient.get(`${BASE_URL}/vehicules`),
        apiClient.get(`${BASE_URL}/users`),
      ]);
      const vehiclesList = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : [];
      const usersList = Array.isArray(usersRes.data) ? usersRes.data : [];
      const ambulanciersList = usersList.filter((u) => u.rôle === "ambulancier");
      const ambulancierIds = new Set(
        ambulanciersList
          .map((u) => Number(u.id))
          .filter((id) => !Number.isNaN(id) && id > 0)
      );

      const hasValidAmbulancier = (vehicle) => {
        const raw = vehicle?.user_id ?? vehicle?.ambulancier_id;
        if (raw === null || raw === undefined || raw === "") return false;
        const idNum = Number(raw);
        if (Number.isNaN(idNum) || idNum <= 0) return false;
        return ambulancierIds.has(idNum);
      };

      const isVehicleAvailable = (vehicle) => {
        const rawStatus =
          vehicle?.statut ??
          vehicle?.status ??
          vehicle?.etat ??
          vehicle?.disponibilité ??
          vehicle?.disponibilite ??
          vehicle?.état;
        if (rawStatus === null || rawStatus === undefined || rawStatus === "") return false;
        const normalized = String(rawStatus).trim().toLowerCase();
        return normalized.includes("disponible") || normalized.includes("available");
      };

      setVehicles(vehiclesList.filter((v) => hasValidAmbulancier(v) && isVehicleAvailable(v)));
      setAllAmbulanciers(ambulanciersList);
    } catch (e) {
      setVehicles([]);
      setAllAmbulanciers([]);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données d'affectation.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const handleCloseAffectation = () => {
    setAssigningMission(false);
    setSelectedAmbulanceId("");
    onAffectClose();
  };

  const refreshDossier = async () => {
    if (!id) return;
    try {
      const dossierRes = await apiClient.get(`${BASE_URL}/dossier/${id}`);
      setDossier(dossierRes.data || null);
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    if (!id) return undefined;

    const socket = io(getSocketServerUrl(), {
      withCredentials: true,
      transports: ["websocket"],
    });

    const shouldHandleForCurrentId = (payload) => {
      const incomingId =
        payload?.id ??
        payload?.dossier_id ??
        payload?.dossierId ??
        payload?.dossier?.id ??
        payload?.data?.id ??
        payload?.data?.dossier_id ??
        payload?.data?.dossier?.id;
      if (!incomingId) return false;
      return String(incomingId) === String(id);
    };

    socket.on("connect", () => {
      socket.emit("join_dossiers");
      socket.emit("join_room", "dossiers");
      socket.emit("join_room", { room: "dossiers" });
      socket.emit("join_dossier_room", { dossierId: id });
      socket.emit("join_dossier_room", id);
      socket.emit("join_room", `dossier_${id}`);
      socket.emit("join_room", { room: `dossier_${id}` });
    });

    const onUpdated = (payload) => {
      if (shouldHandleForCurrentId(payload)) {
        refreshDossier();
      }
    };

    const onDeleted = (payload) => {
      if (!shouldHandleForCurrentId(payload)) return;
      toast({
        title: "Dossier supprimé",
        description: "Ce dossier n'existe plus.",
        status: "warning",
        isClosable: true,
      });
      navigate("/admin/dossier-regulation");
    };

    socket.on("dossier:updated", onUpdated);
    socket.on("dossier:decision-updated", onUpdated);
    socket.on("dossier:deleted", onDeleted);

    return () => {
      socket.disconnect();
    };
  }, [id, navigate, toast]);

  const handleCreateMissionFromDossier = async () => {
    if (!dossier?.id) return;
    if (!selectedVehicle?.id || !selectedVehicle?.user_id) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner une ambulance avec ambulancier assigné.",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    try {
      setAssigningMission(true);
      await apiClient.post(`${BASE_URL}/dossier/${dossier.id}/creemission`, {
        ambulance_id: Number(selectedVehicle.id),
        ambulancier_id: Number(selectedVehicle.user_id),
      });
      toast({
        title: "Mission créée",
        description: "La mission a été créée avec succès.",
        status: "success",
        isClosable: true,
      });
      handleCloseAffectation();
      await refreshDossier();
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de créer la mission depuis ce dossier.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setAssigningMission(false);
    }
  };

  const formatSexe = (sexe) => {
    const s = String(sexe || "").trim().toLowerCase();
    if (["m", "male", "homme", "h"].includes(s)) return "Homme";
    if (["f", "female", "femme"].includes(s)) return "Femme";
    return sexe || "---";
  };

  const formatDate = (value) => {
    if (!value) return "---";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("fr-FR");
  };

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "---";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "---";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const formatOuiNon = (value) => {
    if (value === 1 || value === "1" || value === true) return "Oui";
    if (value === 0 || value === "0" || value === false) return "Non";
    return "---";
  };

  const getUserFullName = (userId, fallbackName = "Utilisateur") => {
    if (!userId) return "---";
    const user = usersById[String(userId)];
    if (user) {
      const fullName = `${user.nom || ""} ${user.prenom || ""}`.trim();
      if (fullName) return fullName;
    }
    return `${fallbackName} #${userId}`;
  };

  const getStatusBg = (statut) => {
    const value = String(statut || "").toLowerCase();
    if (value.includes("attente")) return "#F6B100";
    if (value.includes("mission")) return "#459CE9";
    if (value.includes("term")) return "#29BACD";
    return "#656C7A";
  };

  const normalizedDecision = String(dossier?.decision || "")
    .trim()
    .toLowerCase();

  const patient = patientById || dossier?.patient || null;
  const isCallerPatient =
    dossier?.appelantEstPatient === 1 ||
    dossier?.appelantEstPatient === "1" ||
    dossier?.appelantEstPatient === true;

  const patientName = useMemo(() => {
    if (!patient) return dossier?.patient_id ? `Patient #${dossier.patient_id}` : "---";
    const fullName = `${patient.nom || ""} ${patient.prenom || ""}`.trim();
    return fullName || `Patient #${dossier?.patient_id}`;
  }, [patient, dossier]);
  const isSecondaire = String(dossier?.transportType || "").trim().toLowerCase() === "secondaire";

  if (loading) {
    return (
      <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
        <Flex align="center" justify="center" minH="280px">
          <Spinner />
        </Flex>
      </Box>
    );
  }

  if (error || !dossier) {
    return (
      <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
        <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
          <Text color="red.500" mb="16px">{error || "Dossier introuvable."}</Text>
          <Button variant="outline" onClick={() => navigate("/admin/dossier-regulation")}>
            Retour liste
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box p="20px" pt="120px" bg="gray.50" minH="100vh">
      <Box mb="20px">
        <HStack spacing="10px" mb="20px" align="center">
          <IconButton
            icon={<Icon as={MdArrowBack} />}
            variant="ghost"
            size="sm"
            onClick={() => navigate("/admin/dossier-regulation")}
            aria-label="Retour"
            w="36px"
            h="36px"
            borderRadius="6px"
            _hover={{ bg: "gray.100" }}
          />
          <Text fontSize="14px" color="#656C7A" cursor="pointer" _hover={{ color: "#455879" }} onClick={() => navigate("/admin/dossier-regulation")}>
            Dossier de régulation
          </Text>
          <Icon as={MdArrowForward} w="12px" h="12px" color="#656C7A" transform="rotate(-90deg)" />
          <Text fontSize="14px" color="#455879" fontWeight="600">Détails du dossier</Text>
        </HStack>

        <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
          <Flex justify="space-between" align="flex-start" mb="14px" wrap="wrap" gap="14px">
            <HStack spacing="20px">
              <Box w="72px" h="72px" borderRadius="6px" bg="#E3F2FD" display="flex" alignItems="center" justifyContent="center">
                <Icon as={MdInfo} w="36px" h="36px" color="#459CE9" />
              </Box>
              <VStack align="flex-start" spacing="4px">
                <Text fontSize="20px" fontWeight="600" color="#455879">
                  Réf. Dossier DR-{String(dossier.id || "").padStart(4, "0")}
                </Text>
                <HStack spacing="20px" wrap="wrap">
                  <HStack spacing="5px">
                    <Icon as={MdCalendarToday} w="16px" h="16px" color="#656C7A" />
                    <Text fontSize="14px" color="#656C7A">Créé le: {formatDate(dossier.créé_le || dossier.created_at)}</Text>
                  </HStack>
                  <HStack spacing="5px">
                    <Avatar size="sm" name={dossier.créé_par ? getUserFullName(dossier.créé_par) : "ARM"} />
                    <Text fontSize="14px" color="#455879">Créé par: {dossier.créé_par ? getUserFullName(dossier.créé_par) : displayValue(dossier.created_by_full_name)}</Text>
                  </HStack>
                  <HStack spacing="5px">
                    <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                    <Text fontSize="14px" color="#455879">Timer: {displayValue(dossier.timer)}</Text>
                  </HStack>
                </HStack>
              </VStack>
            </HStack>

            <HStack spacing="10px">
              <Badge bg={String(dossier.cause || "").toLowerCase().includes("u0") || String(dossier.cause || "").toLowerCase().includes("u1") ? "#F8285A" : "#F6B100"} color="white" borderRadius="20px" px="10px" py="6px" fontSize="12px">
                {(dossier.cause || "---").toString().toUpperCase()}
              </Badge>
              <Badge bg={getStatusBg(dossier.statut)} color="white" borderRadius="20px" px="10px" py="6px" fontSize="12px">
                {displayValue(dossier.statut).toUpperCase()}
              </Badge>
              {String(dossier.statut || "").trim().toLowerCase() === "attente affectation" && (
                <Button
                  bg="#006133"
                  color="white"
                  h="36px"
                  borderRadius="6px"
                  px="10px"
                  py="9px"
                  fontSize="13px"
                  fontWeight="500"
                  fontFamily="Poppins, sans-serif"
                  lineHeight="12px"
                  _hover={{ bg: "#004f2aff" }}
                  onClick={handleOpenAffectation}
                >
                  Affectation
                </Button>
              )}
              {canTakeDecision && String(dossier.statut || "").trim().toLowerCase() === "attente medecin" && (
                <Button
                  bg="#006133"
                  color="white"
                  h="36px"
                  borderRadius="6px"
                  px="10px"
                  py="9px"
                  fontSize="13px"
                  fontWeight="500"
                  fontFamily="Poppins, sans-serif"
                  lineHeight="12px"
                  _hover={{ bg: "#004f2aff" }}
                  onClick={() => navigate(`/admin/dossier-regulation/decision/${id}`)}
                >
                  Prendre décision
                </Button>
              )}
              <Button size="sm" onClick={() => navigate(`/admin/dossier-regulation/edit/${id}`)}>
                Modifier
              </Button>
            </HStack>
          </Flex>

          <HStack spacing="0" borderTop="1px solid" borderColor={borderColor} pt="10px">
           
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "apercu" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("apercu")}
            >
              Aperçu du dossier
            </Button>
            <Button
              variant="ghost"
              size="sm"
              borderRadius="6px"
              _hover={{ bg: "gray.100" }}
              bg={activeTab === "decision" ? "gray.50" : "transparent"}
              onClick={() => setActiveTab("decision")}
            >
              Décision
            </Button>
          </HStack>
        </Box>
      </Box>

      {activeTab === "decision" && (
        <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }} mb="20px">
          <Box flex="1" minW={{ base: "100%", xl: "880px" }}>
            <VStack spacing="16px" align="stretch">
              <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} overflow="hidden">
                <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="16px" fontWeight="600" color="#455879">Compléments cliniques - Bilan médecin</Text>
                </Box>
                <Box p="16px">
                  <VStack align="stretch" spacing="14px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Interrogatoire médical</Text>
                      <Textarea value={displayValue(dossier.interrogatoire)} readOnly minH="74px" />
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Hypothèse diagnostique</Text>
                      <Textarea value={displayValue(dossier.hypothese)} readOnly minH="74px" />
                    </Box>
                  </VStack>
                </Box>
              </Box>

              <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} overflow="hidden">
                <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="16px" fontWeight="600" color="#455879">Conduite à tenir médicale</Text>
                </Box>
                <Box p="16px">
                  <VStack align="stretch" spacing="14px">
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Conduite à tenir</Text>
                      <Textarea value={displayValue(dossier.conduite)} readOnly minH="74px" />
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Contre-indications au transport</Text>
                      <Textarea value={displayValue(dossier.contre_indications)} readOnly minH="74px" />
                    </Box>
                  </VStack>
                </Box>
              </Box>
            </VStack>
          </Box>

          <Box w={{ base: "100%", xl: "520px" }} position={{ base: "static", xl: "sticky" }} top={{ xl: "220px" }}>
            <Box bg={bgCard} borderRadius="12px" border="1px solid" borderColor={borderColor} p="12px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="12px">Décision de régulation</Text>
              <VStack align="stretch" spacing="10px" mb="12px">
                {[
                  { id: "accepter", title: "SMUR — Service Mobile Urgence Réanimation", desc: "Équipe médicalisée (médecin ou infirmier anesthésiste)", icon: "🚑" },
                  { id: "avis", title: "Avis", desc: "Avis médical seul, sans transport", icon: "💬" },
                  { id: "refus", title: "Refus", desc: "Transport non indiqué", icon: "⛔" },
                ].map((option) => {
                  const selected = normalizedDecision === option.id;
                  return (
                    <Box
                      key={option.id}
                      border="1px solid"
                      borderColor={selected ? "#2B6CF0" : "gray.200"}
                      borderRadius="10px"
                      p="10px 12px"
                      bg={selected ? "#F5F9FF" : "white"}
                    >
                      <HStack justify="space-between" align="flex-start" spacing="10px">
                        <HStack align="flex-start" spacing="10px">
                          <Box minW="40px" h="40px" borderRadius="8px" border="1px solid" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center" fontSize="18px">
                            {option.icon}
                          </Box>
                          <VStack align="flex-start" spacing="2px">
                            <Text fontSize="14px" fontWeight="700" color="#455879" lineHeight="1.2">{option.title}</Text>
                            <Text fontSize="13px" color="#656C7A" lineHeight="1.2">{option.desc}</Text>
                          </VStack>
                        </HStack>
                        <Circle size="18px" border="2px solid" borderColor={selected ? "#2B6CF0" : "gray.300"} bg={selected ? "#2B6CF0" : "transparent"}>
                          {selected ? <Circle size="7px" bg="white" /> : null}
                        </Circle>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>

              <Box>
                <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Motif de clôture</Text>
                <Textarea value={displayValue(dossier.motif_de_cloture)} readOnly minH="90px" />
              </Box>
            </Box>
          </Box>
        </Flex>
      )}

      {activeTab === "apercu" && (
        <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }}>
          <Box flex="1" minW={{ base: "100%", xl: "1040px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">Contexte de Régulation</Text>
              <Text fontSize="14px" color="#656C7A" mb="20px">Contexte clinique et notes complémentaires</Text>
              <VStack spacing="15px" align="stretch" mb="20px">
                <Box bg="#F9F9F9" border="1px solid" borderColor="gray.200" borderRadius="6px" p="16px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="700" textTransform="uppercase" mb="10px" letterSpacing="0.5px">
                    Contexte Clinique
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="10px">
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Motif de transfert*</Text>
                      <Text fontSize="14px" color="#455879">{displayValue(dossier.motif)}</Text>
                    </Box>
                    {isSecondaire && (
                      <Box gridColumn={{ base: "auto", md: "span 2", xl: "span 2" }}>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Observation brève</Text>
                        <Text fontSize="14px" color="#455879" whiteSpace="pre-wrap">
                          {displayValue(dossier.observation)}
                        </Text>
                      </Box>
                    )}
                    {!isSecondaire && (
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Circonstances</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.circonstances)}</Text>
                      </Box>
                    )}
                    {!isSecondaire && (
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Gestes déjà effectués</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.gestes)}</Text>
                      </Box>
                    )}
                    {!isSecondaire && (
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Danger sur les lieux</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.danger)}</Text>
                      </Box>
                    )}
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Ville</Text>
                      <Text fontSize="14px" color="#455879">{displayValue(dossier.ville)}</Text>
                    </Box>
                    {!isSecondaire && (
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Victimes</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.victimes)}</Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>
                <Box bg="#F9F9F9" border="1px solid" borderColor="gray.200" borderRadius="6px" p="16px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="6px">
                    Notes complémentaires pour le médecin régulateur
                  </Text>
                  <Text fontSize="14px" color="#455879">
                    {displayValue(dossier.note)}
                  </Text>
                </Box>
              </VStack>
            </Box>

            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Informations du dossier
              </Text>
              <VStack spacing="25px" align="stretch">
                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Détails de régulation
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing="20px">
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Type de transport</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879" fontWeight="600">{displayValue(dossier.transportType)}</Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Niveau d'urgence</Text>
                      </HStack>
                      <Text fontSize="14px" color="#455879">{displayValue(dossier.cause)}</Text>
                    </Box>
                    <Box>
                      <HStack spacing="5px" mb="5px">
                        <Icon as={MdTimer} w="16px" h="16px" color="#656C7A" />
                        <Text fontSize="13px" color="#656C7A" fontWeight="500">Urgence / État</Text>
                      </HStack>
                      <HStack spacing="10px">
                        <Badge bg={String(dossier.urgence || "").toLowerCase().includes("u0") || String(dossier.urgence || "").toLowerCase().includes("u1") ? "#F8285A" : "gray.400"} color="white" borderRadius="4px" px="8px" fontSize="10px">
                          {displayValue(dossier.urgence).toUpperCase()}
                        </Badge>
                        <Badge bg={getStatusBg(dossier.statut)} color="white" borderRadius="4px" px="8px" fontSize="10px">
                          {displayValue(dossier.statut).toUpperCase()}
                        </Badge>
                      </HStack>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Origine / Destination
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
                    <Box>
                      <Text fontSize="13px" color="#9AA7BF" fontWeight="700" textTransform="uppercase" mb="10px">Point de départ / Origine</Text>
                      <VStack align="stretch" spacing="10px">
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdLocationOn} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Adresse / Hôpital</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879" fontWeight="500">{displayValue(dossier.adresse)}</Text>
                        </Box>
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdAddBox} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Service / Médecin</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879">
                            {displayValue(dossier.service_a) !== "---" ? `Service: ${displayValue(dossier.service_a)}` : ""}
                            {dossier.doc_a ? `${displayValue(dossier.service_a) !== "---" ? " | " : ""}Médecin: ${getUserFullName(dossier.doc_a, "Médecin")}` : (displayValue(dossier.service_a) !== "---" ? "" : "Non spécifié")}
                          </Text>
                        </Box>
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdLocationCity} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Ville</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879">{displayValue(dossier.ville)}</Text>
                        </Box>
                      </VStack>
                    </Box>
                    <Box>
                      <Text fontSize="13px" color="#9AA7BF" fontWeight="700" textTransform="uppercase" mb="10px">Destination</Text>
                      <VStack align="stretch" spacing="10px">
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdLocationOn} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Adresse</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879" fontWeight="500">{displayValue(dossier.adresse_destination)}</Text>
                        </Box>
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdAddBox} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Service / Médecin</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879">
                            {displayValue(dossier.service_b) !== "---" ? `Service: ${displayValue(dossier.service_b)}` : ""}
                            {dossier.doc_b ? `${displayValue(dossier.service_b) !== "---" ? " | " : ""}Médecin: ${getUserFullName(dossier.doc_b, "Médecin")}` : (displayValue(dossier.service_b) !== "---" ? "" : "Non spécifié")}
                          </Text>
                        </Box>
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdLocationCity} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Ville</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879">{displayValue(dossier.ville_dest)}</Text>
                        </Box>
                        <Box>
                          <HStack spacing="5px" mb="5px">
                            <Icon as={MdInfo} w="16px" h="16px" color="#656C7A" />
                            <Text fontSize="13px" color="#656C7A" fontWeight="500">Accord obtenu</Text>
                          </HStack>
                          <Text fontSize="14px" color="#455879">{formatOuiNon(dossier.accord)}</Text>
                        </Box>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>

                <Divider />

                <Box>
                  <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="15px" letterSpacing="0.5px">
                    Constantes et contexte
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 4 }} spacing="20px">
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">TA</Text><Text fontSize="14px" color="#455879">{displayValue(dossier.ta)}</Text></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">FC</Text><Text fontSize="14px" color="#455879">{displayValue(dossier.fc)}</Text></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">SpO2</Text><Text fontSize="14px" color="#455879">{displayValue(dossier.spo2)}</Text></Box>
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">Glasgow</Text><Text fontSize="14px" color="#455879">{displayValue(dossier.glasgow)}</Text></Box>
                    {!isSecondaire && (
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">Respire</Text><Text fontSize="14px" color="#455879">{formatOuiNon(dossier.respire)}</Text></Box>
                    )}
                    {!isSecondaire && (
                    <Box><Text fontSize="13px" color="#656C7A" fontWeight="500">Conscient</Text><Text fontSize="14px" color="#455879">{formatOuiNon(dossier.conscient)}</Text></Box>
                    )}
                    <Box gridColumn={{ base: "auto", md: "span 2" }}>
                      <Text fontSize="13px" color="#656C7A" fontWeight="500">Autres constantes / Remarques</Text>
                      <Text fontSize="14px" color="#455879">
                        {displayValue(dossier.autre) !== "---" ? displayValue(dossier.autre) : displayValue(dossier.note)}
                      </Text>
                    </Box>
                  </SimpleGrid>
                </Box>
              </VStack>
            </Box>
          </Box>

          <Box w={{ base: "100%", xl: "300px" }}>
            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor} mb="20px">
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px" borderBottom="1px solid" borderColor="gray.100" pb="10px">
                Patient
              </Text>
              <VStack spacing="16px" align="stretch">
                <HStack spacing="12px">
                  <Avatar size="md" name={patientName} bg="#006133" />
                  <VStack align="flex-start" spacing="2px">
                    <Text fontSize="16px" fontWeight="700" color="#455879">{patientName}</Text>
                    <Badge colorScheme="blue" variant="subtle" borderRadius="full" px="10px">
                      {formatSexe(patient?.sexe)} {patient?.age ? `• ${patient.age} ans` : ""}
                    </Badge>
                  </VStack>
                </HStack>
                <Text fontSize="14px" color="#455879">IPP: {displayValue(patient?.ipp || dossier.ipp)}</Text>
                <HStack spacing="6px"><Icon as={MdPhone} w="15px" h="15px" color="#656C7A" /><Text fontSize="14px" color="#455879">{displayValue(patient?.téléphone || patient?.telephone || dossier.phone)}</Text></HStack>
                <Text fontSize="14px" color="#455879">Patient ID: {displayValue(dossier.patient_id)}</Text>
                {!isCallerPatient && (
                  <Box mt="8px" pt="10px" borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="13px" fontWeight="700" color="gray.400" textTransform="uppercase" mb="8px" letterSpacing="0.5px">
                      Appelant
                    </Text>
                    <VStack align="stretch" spacing="6px">
                      <Text fontSize="14px" color="#455879">Nom: {displayValue(dossier.nomAppelant)}</Text>
                      <Text fontSize="14px" color="#455879">Relation: {displayValue(dossier.relationAppelant)}</Text>
                      <Text fontSize="14px" color="#455879">Téléphone: {displayValue(dossier.telephoneAppelant)}</Text>
                    </VStack>
                  </Box>
                )}
              </VStack>
            </Box>

            <Box bg={bgCard} borderRadius="6px" p="20px" border="1px solid" borderColor={borderColor}>
              <Text fontSize="16px" fontWeight="600" color="#455879" mb="20px">L'équipe assignée</Text>
              <VStack align="stretch" spacing="16px">
                <HStack spacing="10px">
                  <Avatar size="sm" name={dossier.assigne ? getUserFullName(dossier.assigne) : "Assigné"} bg="#3182CE" />
                  <VStack align="flex-start" spacing="5px" flex="1">
                    <Text fontSize="14px" fontWeight="600" color="#455879">
                      {dossier.assigne ? getUserFullName(dossier.assigne) : "---"}
                    </Text>
                    <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                      ASSIGNÉ
                    </Badge>
                  </VStack>
                </HStack>

                <HStack spacing="10px">
                  <Avatar size="sm" name={dossier.créé_par ? getUserFullName(dossier.créé_par) : "ARM"} bg="#6B46C1" />
                  <VStack align="flex-start" spacing="5px" flex="1">
                    <Text fontSize="14px" fontWeight="600" color="#455879">
                      {dossier.créé_par ? getUserFullName(dossier.créé_par) : "---"}
                    </Text>
                    <Badge bg="#459CE9" color="white" borderRadius="20px" px="8px" py="4px" fontSize="11px">
                      ARM CREATEUR
                    </Badge>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          </Box>
        </Flex>
      )}

      <Modal isOpen={isAffectOpen} onClose={handleCloseAffectation} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="1200px">
          <ModalHeader>Affecter la mission - Dossier #{dossier?.id || "-"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p="16px">
            {!missionLocation ? (
              <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p="16px" bg="gray.50">
                <Text color="red.500" fontSize="14px">
                  Position du dossier indisponible. Vérifiez le champ `loc` de ce dossier.
                </Text>
              </Box>
            ) : (
              <AffectationMap
                vehicles={vehicles}
                missionLocation={missionLocation}
                selectedAmbulanceId={selectedAmbulanceId}
                onAmbulanceSelect={setSelectedAmbulanceId}
                doctors={[]}
                ambulanciers={allAmbulanciers}
                fetchVehicleLocation={fetchVehicleLocation}
                nurses={[]}
                selectedDoctorId=""
                selectedNurseId=""
                onDoctorSelect={() => {}}
                onNurseSelect={() => {}}
                typeMission="secondaire"
                typeAmbulance=""
                containerHeight="78vh"
              />
            )}
          </ModalBody>
          <ModalFooter>
            <HStack spacing="10px">
              <Button variant="outline" onClick={handleCloseAffectation}>
                Annuler
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleCreateMissionFromDossier}
                isLoading={assigningMission}
                isDisabled={!missionLocation}
              >
                Créer la mission
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
